/* ============================================================
   SPRING ENERGY — IMMERSIVE LAYER JS (Claude, 2026-07-17)
   Scroll progress bar, scroll-reveal, animated stat counters,
   back-to-top. Everything degrades gracefully and honors
   prefers-reduced-motion.
   ============================================================ */
(function () {
  'use strict';

  var reduce =
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function init() {
    /* ---------- Header paint-order fix (v4, 2026-07-19) ----------
       On the long homepage Chrome intermittently drops the fixed
       header's stacking context while it is STUCK: the header gets
       painted in plain DOM order, so later sections (testimonials,
       hotspots, hero-video) paint over it. z-index (any value),
       isolation, transforms — all ignored in that state; only DOM
       position or the browser TOP LAYER win.
       v4: while `site-header--stuck` is present, the header is placed
       inside a `popover` container shown via showPopover() — the top
       layer paints above ALL stacking contexts by spec, glitch or not.
       When the theme un-sticks the header it returns to its original
       parent, so at rest the markup is 100% stock (nothing for the
       theme's own positioning code to fight — the failure mode of
       v1/v2 — and no in-flow layout impact anywhere — the stuck header
       is position:fixed). Browsers without the Popover API fall back
       to hopping the header to the end of <body> while stuck. Skipped
       in the theme editor. */
    var sh = document.getElementById('SiteHeader');
    var shHome = sh ? sh.parentElement : null;
    if (
      sh &&
      shHome &&
      !(window.Shopify && Shopify.designMode) &&
      'MutationObserver' in window
    ) {
      var shLayer = null;
      if (typeof sh.showPopover === 'function') {
        shLayer = document.createElement('div');
        shLayer.id = 'ir-header-layer';
        shLayer.setAttribute('popover', 'manual');
        document.body.appendChild(shLayer);
      }
      var shRelocate = function () {
        var stuck = sh.classList.contains('site-header--stuck');
        if (stuck) {
          if (shLayer) {
            if (sh.parentElement !== shLayer) shLayer.appendChild(sh);
            try {
              if (!shLayer.matches(':popover-open')) shLayer.showPopover();
            } catch (e) {}
          } else if (sh.parentElement !== document.body) {
            document.body.appendChild(sh);
          }
        } else {
          if (shLayer) {
            try {
              if (shLayer.matches(':popover-open')) shLayer.hidePopover();
            } catch (e) {}
          }
          if (sh.parentElement !== shHome) shHome.appendChild(sh);
        }
      };
      new MutationObserver(shRelocate).observe(sh, {
        attributes: true,
        attributeFilter: ['class']
      });
      shRelocate();
    }

    /* ---------- Back-to-top button ---------- */
    var topBtn = document.createElement('button');
    topBtn.className = 'ir-top';
    topBtn.setAttribute('aria-label', 'Back to top');
    topBtn.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 19V5M5 12l7-7 7 7"/></svg>';
    topBtn.addEventListener('click', function () {
      window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
    });
    document.body.appendChild(topBtn);

    /* ---------- Scroll progress bar + header/topBtn state ---------- */
    var bar = document.createElement('div');
    bar.className = 'ir-progress';
    bar.setAttribute('aria-hidden', 'true');
    document.body.appendChild(bar);

    /* ---------- Hero curtain setup ---------- */
    var featurePanel = document.querySelector('.feature-type1');
    var heroInner = document.querySelector('.slideshow-wrapper, .slideshow');
    var heroSection = heroInner
      ? heroInner.closest('[id^="shopify-section-"]')
      : null;
    if (featurePanel && heroSection) {
      heroSection.classList.add('ir-hero-pin');
      featurePanel.classList.add('ir-glass-panel');
      /* Everything after the hero paints above it */
      var sib = heroSection.nextElementSibling;
      while (sib) {
        if (sib !== featurePanel) sib.classList.add('ir-above');
        sib = sib.nextElementSibling;
      }
    } else if (heroSection) {
      heroSection.classList.add('ir-hero-pin');
    }

    /* ---------- Topo map cursor highlight over the hero photo ---------- */
    (function () {
      if (reduce || !heroSection) return;
      var fine = window.matchMedia(
        '(hover: hover) and (pointer: fine)'
      ).matches;
      var touchMode = !fine || /ir-touch/.test(window.location.hash);
      var cv = document.createElement('canvas');
      cv.className = 'ir-topo';
      cv.setAttribute('aria-hidden', 'true');
      heroSection.appendChild(cv);
      var ctx = cv.getContext('2d');
      if (!ctx) return;
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      var W = 0;
      var H = 0;
      var mask = touchMode ? document.createElement('canvas') : null;
      var mctx = mask ? mask.getContext('2d') : null;

      function ihash(ix, iy) {
        var n = (ix * 374761393 + iy * 668265263) | 0;
        n = ((n ^ (n >> 13)) * 1274126177) | 0;
        return (((n ^ (n >> 16)) >>> 0) % 100000) / 100000;
      }
      function sstep(t) {
        return t * t * (3 - 2 * t);
      }
      function vnoise(x, y) {
        var ix = Math.floor(x);
        var iy = Math.floor(y);
        var fx = x - ix;
        var fy = y - iy;
        var a = ihash(ix, iy);
        var b = ihash(ix + 1, iy);
        var c = ihash(ix, iy + 1);
        var d = ihash(ix + 1, iy + 1);
        var u = sstep(fx);
        var v = sstep(fy);
        return (
          a * (1 - u) * (1 - v) +
          b * u * (1 - v) +
          c * (1 - u) * v +
          d * u * v
        );
      }
      function terrain(x, y) {
        return (
          vnoise(x * 0.0042, y * 0.0042) * 0.62 +
          vnoise(x * 0.0093 + 7.3, y * 0.0093 + 3.1) * 0.27 +
          vnoise(x * 0.021 + 13.7, y * 0.021 + 9.4) * 0.11
        );
      }

      var greenSegs = [];
      var whiteSegs = [];
      function buildMap() {
        var r = heroSection.getBoundingClientRect();
        W = r.width;
        H = r.height;
        cv.width = Math.max(1, Math.round(W * dpr));
        cv.height = Math.max(1, Math.round(H * dpr));
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        if (mask) {
          mask.width = cv.width;
          mask.height = cv.height;
          mctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        }

        var step = 14;
        var nx = Math.ceil(W / step) + 1;
        var ny = Math.ceil(H / step) + 1;
        var g = new Array(nx * ny);
        var mn = 1e9;
        var mx = -1e9;
        for (var j = 0; j < ny; j++) {
          for (var i = 0; i < nx; i++) {
            var val = terrain(i * step, j * step);
            g[j * nx + i] = val;
            if (val < mn) mn = val;
            if (val > mx) mx = val;
          }
        }
        greenSegs = [];
        whiteSegs = [];
        var levels = 11;
        for (var L = 0; L < levels; L++) {
          var iso = mn + ((mx - mn) * (L + 1)) / (levels + 1);
          var out = L % 3 === 2 ? whiteSegs : greenSegs;
          for (var jj = 0; jj < ny - 1; jj++) {
            for (var ii = 0; ii < nx - 1; ii++) {
              var tl = g[jj * nx + ii];
              var tr = g[jj * nx + ii + 1];
              var br = g[(jj + 1) * nx + ii + 1];
              var bl = g[(jj + 1) * nx + ii];
              var idx =
                (tl > iso ? 8 : 0) |
                (tr > iso ? 4 : 0) |
                (br > iso ? 2 : 0) |
                (bl > iso ? 1 : 0);
              if (idx === 0 || idx === 15) continue;
              var x0 = ii * step;
              var y0 = jj * step;
              var tX = x0 + step * ((iso - tl) / (tr - tl));
              var rY = y0 + step * ((iso - tr) / (br - tr));
              var bX = x0 + step * ((iso - bl) / (br - bl));
              var lY = y0 + step * ((iso - tl) / (bl - tl));
              var y1 = y0 + step;
              var x1 = x0 + step;
              switch (idx) {
                case 1:
                case 14:
                  out.push(x0, lY, bX, y1);
                  break;
                case 2:
                case 13:
                  out.push(bX, y1, x1, rY);
                  break;
                case 3:
                case 12:
                  out.push(x0, lY, x1, rY);
                  break;
                case 4:
                case 11:
                  out.push(tX, y0, x1, rY);
                  break;
                case 5:
                  out.push(x0, lY, tX, y0);
                  out.push(bX, y1, x1, rY);
                  break;
                case 6:
                case 9:
                  out.push(tX, y0, bX, y1);
                  break;
                case 7:
                case 8:
                  out.push(x0, lY, tX, y0);
                  break;
                case 10:
                  out.push(tX, y0, x1, rY);
                  out.push(x0, lY, bX, y1);
                  break;
              }
            }
          }
        }
      }
      buildMap();
      window.addEventListener('resize', buildMap);

      var tx = -9999;
      var ty = -9999;
      var cx = tx;
      var cy = ty;
      var ltx = tx;
      var lty = ty;
      var swell = 0;
      var alpha = 0;
      var targetAlpha = 0;
      var running = false;
      var blooms = [];
      var overrideUntil = 0;
      var ox = 0;
      var oy = 0;
      var heroVisible = true;

      function startLoop() {
        if (!running) {
          running = true;
          window.requestAnimationFrame(topoFrame);
        }
      }

      if (touchMode) {
        /* Mobile: keep the topo hidden so it never blurs the hero at load.
           Reveal it ONLY while the visitor is touching the hero (finger
           tracked), then fade it away shortly after the finger lifts. */
        var touching = false;
        if ('IntersectionObserver' in window) {
          var vio = new IntersectionObserver(
            function (entries) {
              heroVisible = entries[0].isIntersecting;
              if (!heroVisible) {
                touching = false;
                targetAlpha = 0;
              }
            },
            { threshold: 0.15 }
          );
          vio.observe(heroSection);
        }
        function reveal(clientX, clientY, isStart) {
          var r = heroSection.getBoundingClientRect();
          var x = clientX - r.left;
          var y = clientY - r.top;
          if (x < 0 || y < 0 || x > W || y > H) return;
          if (isStart) {
            blooms.push({ x: x, y: y, born: performance.now() });
            if (blooms.length > 4) blooms.shift();
          }
          ox = x;
          oy = y;
          targetAlpha = 1;
          overrideUntil = performance.now() + 1500;
          startLoop();
        }
        heroSection.addEventListener(
          'touchstart',
          function (e) {
            if (e.touches && e.touches.length) {
              touching = true;
              reveal(e.touches[0].clientX, e.touches[0].clientY, true);
            }
          },
          { passive: true }
        );
        heroSection.addEventListener(
          'touchmove',
          function (e) {
            if (touching && e.touches && e.touches.length) {
              reveal(e.touches[0].clientX, e.touches[0].clientY, false);
            }
          },
          { passive: true }
        );
        function endTouch() {
          touching = false;
          overrideUntil = performance.now() + 450;
        }
        heroSection.addEventListener('touchend', endTouch, { passive: true });
        heroSection.addEventListener('touchcancel', endTouch, { passive: true });
        heroSection.addEventListener('click', function (e) {
          reveal(e.clientX, e.clientY, true);
        });
      } else {
        heroSection.addEventListener('mousemove', function (e) {
          var r = heroSection.getBoundingClientRect();
          tx = e.clientX - r.left;
          ty = e.clientY - r.top;
          if (cx < -999) {
            cx = tx;
            cy = ty;
            ltx = tx;
            lty = ty;
          }
          targetAlpha = 1;
          startLoop();
        });
        heroSection.addEventListener('mouseleave', function () {
          targetAlpha = 0;
        });
      }

      var PULL_R = 210;
      var cullR2 = PULL_R * 3 * (PULL_R * 3);
      var sigma2 = 2 * PULL_R * PULL_R;
      function drawSegs(target, segs, strokeStyle, lw, pull) {
        target.strokeStyle = strokeStyle;
        target.lineWidth = lw;
        target.beginPath();
        for (var i = 0; i < segs.length; i += 4) {
          var x1 = segs[i];
          var y1 = segs[i + 1];
          var x2 = segs[i + 2];
          var y2 = segs[i + 3];
          var dx = cx - x1;
          var dy = cy - y1;
          var r2 = dx * dx + dy * dy;
          if (r2 < cullR2) {
            var f = pull * Math.exp(-r2 / sigma2);
            x1 += dx * f;
            y1 += dy * f;
            dx = cx - x2;
            dy = cy - y2;
            f = pull * Math.exp(-(dx * dx + dy * dy) / sigma2);
            x2 += dx * f;
            y2 += dy * f;
          }
          target.moveTo(x1, y1);
          target.lineTo(x2, y2);
        }
        target.stroke();
      }

      function topoFrame(ts) {
        var t = ts / 1000;
        if (touchMode) {
          /* Stay at the finger's last position - no ambient drift. Once the
             finger lifts and the short linger passes, fade the topo out. */
          tx = ox;
          ty = oy;
          if (touching) {
            targetAlpha = 1;
          } else if (ts > overrideUntil) {
            targetAlpha = 0;
          }
          if (cx < -999) {
            cx = tx;
            cy = ty;
            ltx = tx;
            lty = ty;
          }
        }
        var ease = touchMode && ts < overrideUntil ? 0.1 : touchMode ? 0.03 : 0.16;
        cx += (tx - cx) * ease;
        cy += (ty - cy) * ease;
        alpha += (targetAlpha - alpha) * 0.07;
        var sp = Math.min(Math.hypot(tx - ltx, ty - lty), 42);
        ltx = tx;
        lty = ty;
        swell += (sp - swell) * 0.09;
        var pull =
          (touchMode && ts < overrideUntil ? 0.26 : touchMode ? 0.12 : 0.2) +
          swell * 0.004;

        ctx.clearRect(0, 0, W, H);
        if (alpha > 0.01) {
          drawSegs(
            ctx,
            greenSegs,
            'rgba(90, 214, 94, ' + alpha * 0.75 + ')',
            1.3,
            pull
          );
          drawSegs(
            ctx,
            whiteSegs,
            'rgba(255, 255, 255, ' + alpha * 0.5 + ')',
            1,
            pull
          );

          ctx.globalCompositeOperation = 'destination-in';
          if (touchMode) {
            mctx.clearRect(0, 0, W, H);
            var spotR0 = 240 + swell * 1.6;
            var sg = mctx.createRadialGradient(cx, cy, 0, cx, cy, spotR0);
            sg.addColorStop(0, 'rgba(0, 0, 0, 1)');
            sg.addColorStop(0.55, 'rgba(0, 0, 0, 0.85)');
            sg.addColorStop(1, 'rgba(0, 0, 0, 0)');
            mctx.fillStyle = sg;
            mctx.fillRect(0, 0, W, H);
            for (var b = blooms.length - 1; b >= 0; b--) {
              var age = (ts - blooms[b].born) / 1300;
              if (age >= 1) {
                blooms.splice(b, 1);
                continue;
              }
              var br = 50 + age * 320;
              var fade = (1 - age) * (1 - age);
              var bg = mctx.createRadialGradient(
                blooms[b].x,
                blooms[b].y,
                0,
                blooms[b].x,
                blooms[b].y,
                br
              );
              bg.addColorStop(0, 'rgba(0, 0, 0, ' + fade + ')');
              bg.addColorStop(0.75, 'rgba(0, 0, 0, ' + fade * 0.9 + ')');
              bg.addColorStop(1, 'rgba(0, 0, 0, 0)');
              mctx.fillStyle = bg;
              mctx.beginPath();
              mctx.arc(blooms[b].x, blooms[b].y, br, 0, Math.PI * 2);
              mctx.fill();
            }
            ctx.drawImage(mask, 0, 0, W, H);
          } else {
            var spotR = 250 + swell * 1.6;
            var spot = ctx.createRadialGradient(cx, cy, 0, cx, cy, spotR);
            spot.addColorStop(0, 'rgba(0, 0, 0, 1)');
            spot.addColorStop(0.55, 'rgba(0, 0, 0, 0.85)');
            spot.addColorStop(1, 'rgba(0, 0, 0, 0)');
            ctx.fillStyle = spot;
            ctx.fillRect(0, 0, W, H);
          }

          ctx.globalCompositeOperation = 'destination-over';
          drawSegs(
            ctx,
            greenSegs,
            'rgba(160, 220, 160, ' + alpha * 0.055 + ')',
            1,
            pull
          );

          ctx.globalCompositeOperation = 'source-over';
          var glowR = 200 + swell * 1.5;
          var g = ctx.createRadialGradient(cx, cy, 0, cx, cy, glowR);
          g.addColorStop(0, 'rgba(122, 214, 110, ' + alpha * 0.12 + ')');
          g.addColorStop(1, 'rgba(122, 214, 110, 0)');
          ctx.fillStyle = g;
          ctx.beginPath();
          ctx.arc(cx, cy, glowR, 0, Math.PI * 2);
          ctx.fill();
        }
        if (alpha > 0.01 || targetAlpha > 0) {
          window.requestAnimationFrame(topoFrame);
        } else {
          running = false;
          ctx.clearRect(0, 0, W, H);
        }
      }
    })();

    /* Glass engages as soon as the visitor starts scrolling. */
    function glassUpdate() {
      if (!featurePanel || !heroSection) return;
      var top = window.pageYOffset || document.documentElement.scrollTop || 0;
      featurePanel.classList.toggle('ir-over', top > 5);
    }

    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        var h = document.documentElement;
        var top = window.pageYOffset || h.scrollTop || 0;
        var max = h.scrollHeight - h.clientHeight;
        bar.style.transform = 'scaleX(' + (max > 0 ? top / max : 0) + ')';
        document.body.classList.toggle('ir-scrolled', top > 40);
        topBtn.classList.toggle('ir-show', top > 600);
        glassUpdate();
        ticking = false;
      });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    /* ---------- Clickable product names in collection intro cards ---------- */
    var IR_PLINKS = {
      /* gels */
      'canaberry': 'canaberry',
      'acai': 'acai-power-snack-vegan-100-kcal-1',
      'orange': 'orange-power-snack-vegan-100-kcal',
      'hill aid': 'hill-aid-energy-gel-with-30-mg-caffein-100-kcal',
      'long haul': 'long-haul-energy-gel',
      'awesome sauce': 'awesome-sauce-vegan',
      'peach cobbler xl': 'peach-cobbler-gel-210-kcal',
      'speednut': 'speednut-caffeinated-energy-gel',
      'lemon': 'lemon-113-mg-caffein-power-snack-vegan-100-kcal',
      /* drinks */
      'electroride': 'electrolyte-hydration-energy-mix',
      'watermelon mint': 'watermelon-mint-drink-mix',
      'honey lemongrass': 'honey-lemongrass-drink-mix',
      'passionfruit mango': 'pineapple-mango',
      'orange ginger': 'orange-ginger-drink-mix',
      'grapefruit': 'grapefruit-beet-drink-mix',
      'strawberry matcha': 'matcha-strawberry-drink-mix',
      'chocolate raspberry':
        'chocolate-raspberry-recovery-protein-shake-22g-protein',
      'mango strawberry collagen':
        'chocolate-raspberry-recovery-protein-shake-w-adaptogens-22g-protein-copy'
    };
    var descArea = document.querySelector('.collection-description');
    if (descArea) {
      var paras = descArea.querySelectorAll('p');
      Array.prototype.forEach.call(paras, function (p) {
        if (p.children.length > 0) return; /* text-only paragraphs */
        var parts = p.textContent.split('·');
        var anyMatch = false;
        for (var i = 0; i < parts.length; i++) {
          if (IR_PLINKS[parts[i].trim().toLowerCase()]) {
            anyMatch = true;
            break;
          }
        }
        if (!anyMatch) return;
        var frag = document.createDocumentFragment();
        parts.forEach(function (part, idx) {
          if (idx > 0) frag.appendChild(document.createTextNode(' · '));
          var name = part.trim();
          var handle = IR_PLINKS[name.toLowerCase()];
          if (handle) {
            var s = document.createElement('span');
            s.className = 'ir-plink';
            s.setAttribute('role', 'link');
            s.setAttribute('tabindex', '0');
            s.setAttribute('data-href', '/products/' + handle);
            s.textContent = name;
            frag.appendChild(s);
          } else {
            frag.appendChild(document.createTextNode(name));
          }
        });
        p.innerHTML = '';
        p.appendChild(frag);
      });
      descArea.addEventListener('click', function (e) {
        var t = e.target.closest ? e.target.closest('.ir-plink') : null;
        if (t) window.location.href = t.getAttribute('data-href');
      });
      descArea.addEventListener('keydown', function (e) {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        var t = e.target.closest ? e.target.closest('.ir-plink') : null;
        if (t) {
          e.preventDefault();
          window.location.href = t.getAttribute('data-href');
        }
      });
    }

    if (reduce || !('IntersectionObserver' in window)) return;

    /* ---------- Animated stat counters ---------- */
    function countUp(el) {
      if (!el || el.dataset.irDone) return;
      el.dataset.irDone = '1';
      var raw = el.textContent.trim();
      var m = raw.match(/^([^\d]*)([\d.,]+)(.*)$/);
      if (!m) return;
      var num = parseFloat(m[2].replace(/,/g, ''));
      if (isNaN(num)) return;
      var prefix = m[1];
      var suffix = m[3];
      var dur = 1300;
      var start = null;
      function step(ts) {
        if (!start) start = ts;
        var p = Math.min((ts - start) / dur, 1);
        var ease = 1 - Math.pow(1 - p, 3);
        el.textContent = prefix + Math.round(num * ease) + suffix;
        if (p < 1) {
          window.requestAnimationFrame(step);
        } else {
          el.textContent = raw; /* restore exact original */
        }
      }
      window.requestAnimationFrame(step);
    }

    /* ---------- Scroll-reveal ---------- */
    var selectors =
      '.sfp-badge, .sfp-headline, .sfp-body, .sfp-stat, .sfp-cta, ' +
      '.sfp-cta2, .sfp-product-img, .accordion';
    var nodes = Array.prototype.slice.call(
      document.querySelectorAll(selectors)
    );

    var parentCounts = [];
    nodes.forEach(function (el) {
      var entry = null;
      for (var i = 0; i < parentCounts.length; i++) {
        if (parentCounts[i].p === el.parentNode) {
          entry = parentCounts[i];
          break;
        }
      }
      if (!entry) {
        entry = { p: el.parentNode, n: 0 };
        parentCounts.push(entry);
      }
      el.classList.add('ir-reveal');
      el.style.setProperty('--ir-delay', (entry.n * 0.09).toFixed(2) + 's');
      entry.n++;
    });

    var counterArmed = false;
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('ir-in');
          if (entry.target.classList.contains('sfp-stat')) {
            if (!counterArmed) return;
            countUp(entry.target.querySelector('.sfp-stat-number'));
          }
          io.unobserve(entry.target);
        });
      },
      { threshold: 0.2, rootMargin: '0px 0px -40px 0px' }
    );
    nodes.forEach(function (el) {
      io.observe(el);
    });
    function armCounters() {
      if (counterArmed) return;
      counterArmed = true;
      nodes.forEach(function (el) {
        if (!el.classList.contains('sfp-stat')) return;
        var r = el.getBoundingClientRect();
        if (r.top < window.innerHeight * 0.85 && r.bottom > 0) {
          countUp(el.querySelector('.sfp-stat-number'));
          io.unobserve(el);
        }
      });
    }
    if (document.readyState === 'complete') {
      window.setTimeout(armCounters, 200);
    } else {
      window.addEventListener('load', function () {
        window.setTimeout(armCounters, 200);
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
